import CircleIcon from "@mui/icons-material/Circle";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import { Box, Button, Typography } from "@mui/material";
import Api from "api";
import IMGOcta from "assets/images/octa2-shrink.png";
import { LoaderBackdrop } from "components/Loader";
import useUser from "hooks/useUser";
import useMission from "pages/missionTen/hooks";
import { useEffect, useState } from "react";
import { UserActionType } from "reducers/userReducer";

const questions = [
	"I have a clear idea of\nwhere I want my life and career to go.",
	"I feel ready and confident to take my next steps.",
	"I know who I am and what matters most to me.",
	"Thinking about the future makes me feel stuck or stressed"
];

const options = [
	{ label: "Strongly disagree", value: 1 },
	{ label: "Disagree", value: 2 },
	{ label: "Agree", value: 3 },
	{ label: "Strongly agree", value: 4 }
];

function Questionnaire()
{
	const missionContext = useMission();
	const userContext = useUser();
	const [ currentQuestion, setCurrentQuestion ] = useState(0);
	const [ isLoading, setIsloading ] = useState(false);
	const [ selectedButtonIndex, setSelectedButtonIndex ] = useState<number | null>(null);
	const [ nextQuestionIndex, setNextQuestionIndex ] = useState<number | null>(null);
	const [ animating, setAnimating ] = useState(false);
	const [ answers, setAnswers ] = useState({
		clarity: 0,
		confidence: 0,
		selfKnowledge: 0,
		anxiety: 0
	});
	const answerKeys = [ "clarity", "confidence", "selfKnowledge", "anxiety" ] as const;

	// eslint-disable-next-line func-style
	const handleAnswer = (value: number, index: number) =>
	{
		const key = answerKeys[ currentQuestion ];
		const adjustedValue = key === "anxiety" ? 5 - value : value;

		setSelectedButtonIndex(index);

		// Убрать фокус с кнопки
		if (document.activeElement instanceof HTMLElement)
		{
			document.activeElement.blur();
		}

		setTimeout(() =>
		{
			setSelectedButtonIndex(null);
		}, 300);

		setAnswers((prev) => ({
			...prev,
			[ key ]: adjustedValue
		}));

		if (currentQuestion < questions.length - 1)
		{
			setCurrentQuestion((prev) => prev + 1);
		}

		if (currentQuestion < questions.length - 1)
		{
			setAnimating(true);
			setNextQuestionIndex(currentQuestion + 1);
		}
	};

	useEffect(() =>
	{
		if (answers.anxiety > 0)
		{
			setIsloading(true);
			Api.users
				.updateUser({ after: answers })
				.then(() =>
				{
					userContext.dispatch(
						{
							type: UserActionType.UPDATE_STATE,
							payload: { user: { after: answers } }
						}
					);
					missionContext.setStep((prevStep) => prevStep + 1);
					missionContext.setBackground("surpriseBg");
				})
				.catch((error) => console.error(error))
				.finally(() => setIsloading(false));
		}
	}, [ answers ]);

	useEffect(() =>
	{
		if (animating && nextQuestionIndex !== null)
		{
			const timeout = setTimeout(() =>
			{
				setCurrentQuestion(nextQuestionIndex);
				setNextQuestionIndex(null);
				setAnimating(false);
			}, 400);

			return () => clearTimeout(timeout);
		}
	}, [ animating, nextQuestionIndex ]);

	return (
		<Box sx={{
			position: "relative",
			padding: "16px",
			paddingTop: { xl: "88px", xs: "54px" },
			margin: "0 auto",
			maxWidth: { xl: "844px", sm: "784px" },
			width: "100%",
			flexGrow: 1,
			display: "flex",
			flexDirection: "column",
			gap: { sm: "unset", xs: 3 },
			alignItems: "center"
		}}>
			<Box
				component="img"
				src={IMGOcta}
				alt=""
				sx={{
					display: { xs: "none", lg: "block" },
					position: "absolute",
					top: { xl: "94px", lg: "45px" },
					left: { xl: "-106px", lg: "35px" },
					width: { xl: "199px", lg: "130px" },
					transform: "rotate(-15.4deg)",
					zIndex: 9999
				}}
			/>

			<Box sx={{
				textAlign: "center",
				font: { xl: "700 40px/51px Lora-Bold", xs: "700 24px/31px Lora-Bold" },
				color: "primary.main"
			}}>
				{userContext.state.user?.name}, how much do you<br />
				agree with each statement?
			</Box>
			<LoaderBackdrop isLoading={isLoading}>
				<Box
					sx={{
						background: "linear-gradient(103.55deg, rgba(230, 229, 229, 0.79) 9.99%, rgba(239, 239, 239, 0.22) 91.61%)",
						mt: { md: 8, xs: 0 },
						width: { md: "980px", xs: "auto" },
						height: { md: "440px", xs: "auto" },
						border: "1px solid rgba(255, 255, 255, 0.5)",
						borderRadius: "24px",
						backdropFilter: "blur(30px)",
						WebkitBackdropFilter: "blur(30px)",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						padding: { xs: 2, sm: 5 },
						transition: "all 0.4s ease-in-out",
						opacity: animating ? 0 : 1,
						transform: animating ? "translateX(-100px)" : "translateX(0)"
					}}
				>
					<Box sx={{ display: "flex", justifyContent: "center", gap: "4px" }}>
						{[ 0, 1, 2, 3 ].map((item) => (
							<CircleIcon key={item} sx={{ color: item === currentQuestion ? "#3E809D" : "#fff", fontSize: { xs: "10px", md: "19.5px" } }} />
						))}
					</Box>

					<Typography
						sx={{
							font: { md: "700 24px/30px Lora-Bold", xs: "700 18px/24px Lora-Bold" },
							color: "primary.main",
							mt: { md: 10, xs: 5 },
							mb: { md: 10, xs: 4 },
							minHeight: "60px"
						}}
						variant="h6"
						textAlign="center"
						mb={3}
						whiteSpace="pre-line">
						{questions[ currentQuestion ]}
					</Typography>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexDirection: { md: "row", xs: "column" },
							width: "100%",
							gap: { md: 4, xs: 2 },
							overflowX: { xs: "auto", sm: "unset" },
							paddingX: { xs: 1, sm: 0 }
						}}
					>
						<SentimentVeryDissatisfiedIcon sx={{ color: "rgba(33, 92, 117, 1)", fontSize: { md: 32, xs: 27 } }} />

						{/* <Stack
						direction="row"
						spacing={2}
						sx={{
							minWidth: "0",
							flexGrow: 1,
							justifyContent: "center",
							flexWrap: { xs: "nowrap", sm: "wrap" }
						}}
					> */}
						{options.map((option, index) => (
							<Button
								key={`${ currentQuestion }-${ option.value }`} // <--- фикс
								variant="outlined"
								onClick={() => handleAnswer(option.value, index)}
								sx={{
									whiteSpace: "nowrap",
									minWidth: { xs: "10px", sm: "auto" },
									background: "linear-gradient(103.55deg, rgba(230, 229, 229, 0.79) 9.99%, rgba(239, 239, 239, 0.22) 91.61%)",
									border: "1px solid rgba(255, 255, 255, 0.5)",
									borderRadius: "40px",
									boxShadow: selectedButtonIndex === index
										? "0 0 20px 4px rgba(62, 128, 157, 0.6)"
										: "none",
									transition: "box-shadow 0.3s ease-in-out",
									backdropFilter: "blur(60px)",
									color: "#4D4D4D",
									textTransform: "none",
									fontSize: { md: "22px", xs: "16px" },
									p: { md: "11px 20px", xs: "11px 32px" }
								}}
							>
								{option.label}
							</Button>
						))}
						{/* </Stack> */}

						<SentimentSatisfiedAltIcon sx={{ color: "rgba(33, 92, 117, 1)", fontSize: { md: 32, xs: 27 } }} />
					</Box>
				</Box>
			</LoaderBackdrop>
		</Box>
	);
}

export default Questionnaire;
